import { useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/frontend/components/ui/button";
import { Separator } from "@/frontend/components/ui/separator";
import { useUpsertEhrClientIdMutation, useDeleteEhrClientIdMutation } from "@/frontend/mutations/dashboard/settings";
import { getProviderDocsUrl } from "@/frontend/utils/dashboard/settings/ehr-docs";
import { DocsHelpLink } from "./DocsHelpLink";
import { ProviderLogo } from "./ProviderLogo";
import { CustomCredentialsCard } from "./CustomCredentialsCard";
import { CREDENTIAL_MODE } from "@/frontend/types/ehr-clients";
import type { CredentialMode, Provider, SavedRow } from "@/frontend/types/ehr-clients";
import { CLIENT_SECRET_PROVIDERS, type EhrProviderType } from "@/frontend/constants/ehr-providers";

interface Props {
  provider: Provider;
  savedRow: SavedRow | undefined;
}

export function EhrClientDetailView({ provider, savedRow }: Props) {
  const upsert = useUpsertEhrClientIdMutation();
  const deleteClient = useDeleteEhrClientIdMutation();

  // Enabled-by-default: no row OR a row without disabled_at means enabled.
  const isConnectionEnabled = !savedRow?.disabled_at;
  const savedCredentialMode: CredentialMode = savedRow?.client_id ? CREDENTIAL_MODE.OWN : CREDENTIAL_MODE.MEDBLOCKS;
  const savedCustomClientId = savedRow?.client_id ?? "";

  // Parent waits for data to load before rendering this component, so these
  // initializers are stable — no sync effect needed.
  const [credentialMode, setCredentialMode] = useState<CredentialMode>(savedCredentialMode ?? CREDENTIAL_MODE.MEDBLOCKS);
  const [customClientId, setCustomClientId] = useState(savedCustomClientId);
  const [clientSecret, setClientSecret] = useState("");

  const supportsClientSecret = (CLIENT_SECRET_PROVIDERS as readonly string[]).includes(provider.type);

  // Tracks unsaved credential changes — enable/disable are immediate actions, not tracked here
  const hasUnsavedChanges = (() => {
    if (!isConnectionEnabled) return false;
    if (credentialMode !== savedCredentialMode) return true;
    if (credentialMode === CREDENTIAL_MODE.OWN && customClientId.trim() !== savedCustomClientId) return true;
    if (supportsClientSecret && credentialMode === CREDENTIAL_MODE.OWN && clientSecret.trim().length > 0) return true;
    return false;
  })();

  const isClientIdRequired = credentialMode === CREDENTIAL_MODE.OWN && !customClientId.trim();
  const canSaveCredentials = hasUnsavedChanges && !isClientIdRequired;
  const isMutationPending = upsert.isPending || deleteClient.isPending;

  const connectionStatusText = !isConnectionEnabled
    ? "Not connected"
    : savedCredentialMode === CREDENTIAL_MODE.OWN
    ? "Using custom credentials"
    : "Using Medblocks' credentials";

  function handleEnable() {
    // Reactivate preserving any previously-saved custom credentials.
    upsert.mutate({ providerType: provider.type as EhrProviderType, clientId: savedRow?.client_id ?? null });
  }

  function handleDisable() {
    deleteClient.mutate({ providerType: provider.type as EhrProviderType });
  }

  function handleSave() {
    const clientId = credentialMode === CREDENTIAL_MODE.OWN ? customClientId.trim() || null : null;
    if (credentialMode === CREDENTIAL_MODE.OWN && !clientId) return;
    // Client secret: omit unless this provider supports it AND the user typed
    // a new value. Switching back to Medblocks-supplied credentials clears any
    // stored secret alongside the client ID.
    let clientSecretUpdate: string | null | undefined = undefined;
    if (credentialMode !== CREDENTIAL_MODE.OWN) {
      clientSecretUpdate = null;
    } else if (supportsClientSecret && clientSecret.trim().length > 0) {
      clientSecretUpdate = clientSecret.trim();
    }
    upsert.mutate({ providerType: provider.type as EhrProviderType, clientId, clientSecret: clientSecretUpdate });
    setClientSecret("");
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const redirectUri = `${origin}/api/auth/smart/callback`;
  const jwksUri = `${origin}/api/auth/.well-known/jwks`;
  const docsUrl = getProviderDocsUrl(provider.type);

  return (
    <div className="flex flex-col gap-8">
      <Link
        to="/dashboard/settings"
        search={{ section: "clients", provider: "", destinationId: undefined }}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-4" />
        Back to Connections
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex size-10 items-center justify-center rounded-lg border bg-white p-1.5 shrink-0">
            <ProviderLogo provider={provider.type} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">{provider.label}</h2>
              <DocsHelpLink
                href={docsUrl}
                tooltip={provider.description}
                ariaLabel={`Open ${provider.label} integration guide`}
              />
              {isConnectionEnabled ? (
                <span className="inline-flex items-center rounded-md bg-green-50 border border-green-200 px-2 py-0.5 text-xs font-medium text-green-700">
                  Enabled
                </span>
              ) : (
                <span className="inline-flex items-center rounded-md bg-red-50 border border-red-200 px-2 py-0.5 text-xs font-medium text-red-700">
                  Disabled
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{connectionStatusText}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasUnsavedChanges ? (
            <Button size="sm" onClick={handleSave} disabled={isMutationPending || !canSaveCredentials}>
              {upsert.isPending && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
              Save
            </Button>
          ) : !isConnectionEnabled ? (
            <Button size="sm" onClick={handleEnable} disabled={isMutationPending}>
              {upsert.isPending && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
              Enable connection
            </Button>
          ) : null}
        </div>
      </div>

      <Separator />

      {/* About */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground mb-3">About</p>
        <p className="text-sm text-muted-foreground">{provider.description}</p>
      </div>

      {/* Card: credentials (only when enabled) */}
      {isConnectionEnabled && (
        <CustomCredentialsCard
          enabled={credentialMode === CREDENTIAL_MODE.OWN}
          onToggle={() => setCredentialMode(credentialMode === CREDENTIAL_MODE.OWN ? CREDENTIAL_MODE.MEDBLOCKS : CREDENTIAL_MODE.OWN)}
          clientId={customClientId}
          onClientIdChange={setCustomClientId}
          registrationUrl={provider.registrationUrl}
          providerLabel={provider.label}
          docsUrl={docsUrl}
          redirectUri={redirectUri}
          jwksUri={jwksUri}
          disabled={isMutationPending}
          error={upsert.isError ? upsert.error.message : null}
          hasSecret={Boolean(savedRow?.has_secret)}
          clientSecret={supportsClientSecret ? clientSecret : undefined}
          onClientSecretChange={supportsClientSecret ? setClientSecret : undefined}
        />
      )}

      {/* Scopes */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground mb-3">Scopes</p>
        <div className="flex flex-wrap gap-1.5">
          {provider.scopes.map((scope) => (
            <span
              key={scope}
              className="inline-flex items-center rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1 font-mono text-xs text-slate-600 select-all"
            >
              {scope}
            </span>
          ))}
        </div>
      </div>

      {/* Danger zone (only when enabled) */}
      {isConnectionEnabled && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-destructive mb-3">
            Danger zone
          </div>
          <div className="space-y-2">
            <div className="border border-destructive/30 rounded-lg p-3">
              <p className="text-sm font-medium mb-1">Disable connection</p>
              <p className="text-xs text-muted-foreground mb-3">
                Patients will no longer be able to connect to {provider.label} hospitals. You can re-enable at any time.
              </p>
              <Button
                size="sm"
                variant="destructive"
                className="h-8 text-xs gap-1.5"
                onClick={handleDisable}
                disabled={isMutationPending}
              >
                {deleteClient.isPending && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
                Disable
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
